import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_DATA_PROTECTION_EXCLUSIONS_GRID } from '../../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  addressCellCustomRenderer,
  Column,
  ColumnKeys,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  PREDEFINED_FILTERS_KEYS,
} from './grid-config';
import {
  ContentControlDataProtectionExclusionFrontendFields,
  GzContentControlDataProtectionExclusionsGridDataProvider,
} from './GzContentControlDataProtectionExclusionsGridDataProvider';
import { html, TemplateResult } from 'lit';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import {
  ContentControlDataProtectionExclusion,
  ContentControlDataProtectionRule,
} from '@nirvana/shared/dtos/types/policies/common';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../../ui/gzButton/types';
import { GzButtonWebComponent } from '../../../../../../../../ui/gzButton';
import { FontSizes, FontWeights } from '../../../../../../../../types/common/types';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzContentControlWebDataProtectionExclusionsFormWebComponent } from './rulesItemForm';
import { validateNameDuplicates, validateSelectedRules } from './utils/validators';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { getSanitizedItemWithoutAdditionalFrontendFields } from './utils/converters';

@customElement(WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_DATA_PROTECTION_EXCLUSIONS_GRID)
export class GzContentControlDataProtectionExclusionsGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  ContentControlDataProtectionExclusion,
  ContentControlDataProtectionExclusion,
  GzContentControlDataProtectionExclusionsGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected columnRenderers = {
    [ColumnKeys.ADDRESS]: addressCellCustomRenderer.bind(null, this.onRuleNameClick.bind(this)),
  };
  protected showHeaderCustomActionsInsideGrid = true;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

  protected hasSelectableRows = true;
  protected hasBasicEditRowAction = true;
  protected hasEditRowAction = false;

  protected translations = {
    editRow: translateFn('Edit'),
    deleteRow: translateFn('Delete'),
  };

  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;

  private currentBodyForm: GzContentControlWebDataProtectionExclusionsFormWebComponent;

  private dataProtectionRulesField: GzFormFieldWebComponent;

  public set fieldWithRulesToConsider(dataProtectionRulesField: GzFormFieldWebComponent) {
    this.dataProtectionRulesField = dataProtectionRulesField;
  }

  protected getDataProvider(): GzContentControlDataProtectionExclusionsGridDataProvider {
    return new GzContentControlDataProtectionExclusionsGridDataProvider(this.initialGridItems);
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);
    this.deleteButton.disabled = !event.detail.selectionStoreData.length;
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return html`
      <gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.LIGHT}"
        >${translateFn('No exclusions')}</gz-text
      >

      <gz-button
        id="empty-state-add-button"
        type="${ButtonTypes.QUIET}"
        buttonHeight="${ButtonHeights.SMALL}"
        buttonFontSize="${ButtonFontSizes.SMALL}"
        style="margin-top: 5px;"
        @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Adjust your filters or start adding exclusions.')}
      </gz-button>
    `;
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <gz-button id="add-button" type="${ButtonTypes.OUTLINE}" @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Add')}
      </gz-button>

      <gz-button
        id="delete-button"
        type="${ButtonTypes.OUTLINE}"
        color="${ButtonColors.NEGATIVE}"
        ?disabled
        @click="${this.onDeleteSelectedItemsWithDialog.bind(
          this,
          translateFn('Delete exclusions?'),
          translateFn('You are about to delete the selected exclusions. This action cannot be undone.')
        )}"
        >${translateFn('Delete')}
      </gz-button>
    `;
  }

  private onAddButtonClick(): void {
    const addRuleCallback = (formValue: ContentControlDataProtectionExclusion): ValidationResult =>
      this.onNewGridItemAdd(formValue);

    // create configs
    this.currentBodyForm = new GzContentControlWebDataProtectionExclusionsFormWebComponent(
      this.dataProtectionRulesField.value as Array<ContentControlDataProtectionRule>
    );
    const configs: FullPagePopupConfigs = {
      bodyForm: this.currentBodyForm,
      strings: {
        title: translateFn('Add data protection exclusion'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  protected onBasicEditRow(event: CustomEvent) {
    this.onRuleNameClick(event.detail.rowData.item);
  }

  private onRuleNameClick(item: ContentControlDataProtectionExclusion): void {
    const addRuleCallback = (formValue: ContentControlDataProtectionExclusion): ValidationResult =>
      this.onEditGridItemFromPopup(formValue, item);

    // create configs
    this.currentBodyForm = new GzContentControlWebDataProtectionExclusionsFormWebComponent(
      this.dataProtectionRulesField.value as Array<ContentControlDataProtectionRule>,
      item
    );
    const configs: FullPagePopupConfigs = {
      bodyForm: this.currentBodyForm,
      strings: {
        title: translateFn('Edit data protection exclusion'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: ContentControlDataProtectionExclusion): ValidationResult {
    let isValid = true;

    // check for duplicates
    const nameDuplicatesValidation = validateNameDuplicates(gridItemFormValue, this.getFormValue());
    if (!nameDuplicatesValidation.valid) {
      this.currentBodyForm.setItemNameError(nameDuplicatesValidation.error);
      isValid = false;
    }

    // at least one rule must be selected
    const stringDuplicatesValidation = validateSelectedRules(gridItemFormValue);
    if (!stringDuplicatesValidation.valid) {
      this.currentBodyForm.setRulesToConsiderError(stringDuplicatesValidation.error);
      isValid = false;
    }

    return isValid ? { valid: true } : { valid: false, error: '' };
  }

  protected sanitizeGridItemBeforeEdit(
    gridItem: ContentControlDataProtectionExclusionFrontendFields
  ): ContentControlDataProtectionExclusion {
    return getSanitizedItemWithoutAdditionalFrontendFields(gridItem);
  }

  protected validateGridItemBeforeEdit(
    oldItem: ContentControlDataProtectionExclusion,
    newItem: ContentControlDataProtectionExclusion
  ): ValidationResult {
    // skip if item is not changed
    const hasSameName = oldItem.address === newItem.address;
    const hasSelection = newItem.rulesToConsider.length > 0;
    if (hasSameName && hasSelection) {
      return { valid: true };
    }

    let isValid = true;

    // check for duplicates if name is changed
    const nameDuplicatesValidation: ValidationResult = hasSameName
      ? { valid: true }
      : validateNameDuplicates(newItem, this.getFormValue());
    if (!nameDuplicatesValidation.valid) {
      this.currentBodyForm.setItemNameError(nameDuplicatesValidation.error);
      isValid = false;
    }

    // at least one rule must be selected
    if (!hasSelection) {
      this.currentBodyForm.setRulesToConsiderError(translateFn('Select at least one rule for this exclusion.'));
      isValid = false;
    }

    return isValid ? { valid: true } : { valid: false, error: '' };
  }
}
